import { useMemo } from 'react';
import { formatTimeAgo } from '../../../utils/dateUtils';
import { ActivityType, AuthorType, getUserFriendlyStatus } from '../../../types/ticketActivity';
import type { TicketActivity, TicketAttachment } from '../../../types/ticketActivity';
import { QuestionPrompt } from './QuestionPrompt';
import './ActivityTimeline.css';

interface ActivityTimelineProps {
  activities: TicketActivity[];
  currentUserId: string;
  onAnswerQuestion?: (questionId: string, answer: string) => Promise<void>;
}

/**
 * ActivityTimeline - Chronological feed of ticket events
 *
 * Displays:
 * - User submissions and replies
 * - IT staff replies
 * - Status changes (in plain language)
 * - Attachments
 * - System events (minimized)
 * - Questions from IT with answer prompts
 *
 * Activities are sorted descending (newest first) by the parent component.
 */
export function ActivityTimeline({ activities, currentUserId, onAnswerQuestion }: ActivityTimelineProps) {
  // Filter out internal-only activities (already sorted by parent)
  const visibleActivities = useMemo(() => {
    return activities.filter((activity) => activity.visibility === 'public');
  }, [activities]);

  if (visibleActivities.length === 0) {
    return (
      <section className="activity-timeline" aria-label="Activity">
        <h2 className="activity-timeline__heading">Activity</h2>
        <div className="activity-timeline__empty">
          <p>No activity yet. We'll update you as soon as there's progress.</p>
        </div>
      </section>
    );
  }

  return (
    <section className="activity-timeline" aria-label="Activity">
      <h2 className="activity-timeline__heading">Activity</h2>
      <div className="activity-timeline__list" role="list">
        {visibleActivities.map((activity, index) => (
          <ActivityItem
            key={activity.id}
            activity={activity}
            isCurrentUser={activity.authorId === currentUserId}
            isFirst={index === 0}
            isLast={index === visibleActivities.length - 1}
            onAnswerQuestion={onAnswerQuestion}
          />
        ))}
      </div>
    </section>
  );
}

interface ActivityItemProps {
  activity: TicketActivity;
  isCurrentUser: boolean;
  isFirst: boolean;
  isLast: boolean;
  onAnswerQuestion?: (questionId: string, answer: string) => Promise<void>;
}

function ActivityItem({ activity, isCurrentUser, isFirst, isLast, onAnswerQuestion }: ActivityItemProps) {
  const isSystemActivity =
    activity.type === ActivityType.STATUS_CHANGE ||
    activity.type === ActivityType.ASSIGNED ||
    activity.type === ActivityType.SYSTEM;

  const isResolved = activity.type === ActivityType.RESOLVED;
  const isQuestion = activity.type === ActivityType.QUESTION_TO_USER;
  const isAnswer = activity.type === ActivityType.ANSWER_FROM_USER;

  // Question activity - special treatment with prompt or answered state
  if (isQuestion) {
    return (
      <div className="activity-timeline__item activity-timeline__item--question" role="listitem">
        <div className="activity-timeline__connector">
          <div className={`activity-timeline__line ${isFirst ? 'activity-timeline__line--hidden' : ''}`} />
          <div className="activity-timeline__dot activity-timeline__dot--question">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
              <path d="M8 0a8 8 0 100 16A8 8 0 008 0zm0 12a1 1 0 110-2 1 1 0 010 2zm1-3.5a1 1 0 01-2 0V5a1 1 0 012 0v3.5z" />
            </svg>
          </div>
          <div className={`activity-timeline__line ${isLast ? 'activity-timeline__line--hidden' : ''}`} />
        </div>
        <div className="activity-timeline__question-content">
          {!activity.isAnswered && onAnswerQuestion ? (
            <QuestionPrompt
              questionId={activity.id}
              questionText={activity.questionText ?? activity.content ?? ''}
              authorName={activity.authorName}
              createdAt={activity.createdAt}
              onSubmitAnswer={onAnswerQuestion}
            />
          ) : (
            <div className="activity-timeline__answered-question">
              <div className="activity-timeline__question-header">
                <span className="activity-timeline__question-label">
                  Question from {activity.authorName}
                </span>
                <span className="activity-timeline__time">{formatTimeAgo(activity.createdAt)}</span>
              </div>
              <p className="activity-timeline__question-text">
                {activity.questionText ?? activity.content}
              </p>
              {activity.isAnswered && (
                <span className="activity-timeline__answered-badge">Answered</span>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Answer activity - display as user comment with special context
  if (isAnswer) {
    return (
      <div className="activity-timeline__item activity-timeline__item--user" role="listitem">
        <div className="activity-timeline__connector">
          <div className={`activity-timeline__line ${isFirst ? 'activity-timeline__line--hidden' : ''}`} />
          <div className="activity-timeline__dot activity-timeline__dot--user">
            <AuthorAvatar
              authorType={activity.authorType}
              authorName={activity.authorName}
              isCurrentUser={true}
            />
          </div>
          <div className={`activity-timeline__line ${isLast ? 'activity-timeline__line--hidden' : ''}`} />
        </div>
        <div className="activity-timeline__message">
          <div className="activity-timeline__message-header">
            <span className="activity-timeline__author">You</span>
            <span className="activity-timeline__answer-label">answered</span>
            <span className="activity-timeline__time">{formatTimeAgo(activity.createdAt)}</span>
          </div>
          <div className="activity-timeline__message-body">
            <p className="activity-timeline__text">{activity.content}</p>
          </div>
        </div>
      </div>
    );
  }

  // System activities get a compact treatment
  if (isSystemActivity && !isResolved) {
    return (
      <div className="activity-timeline__item activity-timeline__item--system" role="listitem">
        <div className="activity-timeline__connector">
          <div className={`activity-timeline__line ${isFirst ? 'activity-timeline__line--hidden' : ''}`} />
          <div className="activity-timeline__dot activity-timeline__dot--system">
            <StatusIcon activity={activity} />
          </div>
          <div className={`activity-timeline__line ${isLast ? 'activity-timeline__line--hidden' : ''}`} />
        </div>
        <div className="activity-timeline__system-content">
          <span className="activity-timeline__system-message">
            {getActivityMessage(activity)}
          </span>
          <span className="activity-timeline__system-time">{formatTimeAgo(activity.createdAt)}</span>
        </div>
      </div>
    );
  }

  // Resolved activity gets special treatment
  if (isResolved) {
    return (
      <div className="activity-timeline__item activity-timeline__item--resolved" role="listitem">
        <div className="activity-timeline__connector">
          <div className={`activity-timeline__line ${isFirst ? 'activity-timeline__line--hidden' : ''}`} />
          <div className="activity-timeline__dot activity-timeline__dot--resolved">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 8l4 4 6-8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div className={`activity-timeline__line ${isLast ? 'activity-timeline__line--hidden' : ''}`} />
        </div>
        <div className="activity-timeline__resolved-content">
          <span className="activity-timeline__resolved-badge">Resolved</span>
          <span className="activity-timeline__resolved-time">{formatTimeAgo(activity.createdAt)}</span>
        </div>
      </div>
    );
  }

  // Regular comment/message activity
  return (
    <div
      className={`activity-timeline__item ${isCurrentUser ? 'activity-timeline__item--user' : 'activity-timeline__item--staff'}`}
      role="listitem"
    >
      <div className="activity-timeline__connector">
        <div className={`activity-timeline__line ${isFirst ? 'activity-timeline__line--hidden' : ''}`} />
        <div
          className={`activity-timeline__dot ${isCurrentUser ? 'activity-timeline__dot--user' : 'activity-timeline__dot--staff'}`}
        >
          <AuthorAvatar authorType={activity.authorType} authorName={activity.authorName} isCurrentUser={isCurrentUser} />
        </div>
        <div className={`activity-timeline__line ${isLast ? 'activity-timeline__line--hidden' : ''}`} />
      </div>
      <div className="activity-timeline__message">
        <div className="activity-timeline__message-header">
          <span className="activity-timeline__author">
            {isCurrentUser ? 'You' : activity.authorName}
          </span>
          <span className="activity-timeline__time">{formatTimeAgo(activity.createdAt)}</span>
        </div>
        <div className="activity-timeline__message-body">
          <p className="activity-timeline__text">{activity.content}</p>
          {activity.attachments && activity.attachments.length > 0 && (
            <div className="activity-timeline__attachments">
              {activity.attachments.map((attachment) => (
                <AttachmentItem key={attachment.id} attachment={attachment} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

interface AuthorAvatarProps {
  authorType: AuthorType;
  authorName: string;
  isCurrentUser: boolean;
}

function AuthorAvatar({ authorType, authorName, isCurrentUser }: AuthorAvatarProps) {
  if (authorType === AuthorType.SYSTEM) {
    return (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
        <path d="M8 0a8 8 0 100 16A8 8 0 008 0zm-.75 5a.75.75 0 011.5 0v4a.75.75 0 01-1.5 0V5zm.75 7a.75.75 0 100-1.5.75.75 0 000 1.5z" />
      </svg>
    );
  }

  if (authorType === AuthorType.IT_STAFF && !isCurrentUser) {
    return (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
        <path d="M8 8a3 3 0 100-6 3 3 0 000 6zm5 6a5 5 0 00-10 0h10z" />
      </svg>
    );
  }

  // User avatar - first initial
  return <span className="activity-timeline__avatar-initial">{authorName.charAt(0).toUpperCase()}</span>;
}

interface AttachmentItemProps {
  attachment: TicketAttachment;
}

function AttachmentItem({ attachment }: AttachmentItemProps) {
  const isImage = attachment.mimeType.startsWith('image/');
  const fileSize = formatFileSize(attachment.fileSize);

  return (
    <a
      href={attachment.url}
      className="activity-timeline__attachment"
      target="_blank"
      rel="noopener noreferrer"
      download={attachment.fileName}
    >
      <div className="activity-timeline__attachment-icon">
        {isImage ? (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M14 3v10a1 1 0 01-1 1H3a1 1 0 01-1-1V3a1 1 0 011-1h10a1 1 0 011 1zM3 13l3-4 2 2 4-5 3 4V3H3v10z" />
          </svg>
        ) : (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M4 1a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V5l-4-4H4zm5 0v4h4L9 1z" />
          </svg>
        )}
      </div>
      <div className="activity-timeline__attachment-info">
        <span className="activity-timeline__attachment-name">{attachment.fileName}</span>
        <span className="activity-timeline__attachment-size">{fileSize}</span>
      </div>
    </a>
  );
}

function StatusIcon({ activity }: { activity: TicketActivity }) {
  switch (activity.type) {
    case ActivityType.STATUS_CHANGE:
      return (
        <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
          <path d="M6 0a6 6 0 100 12A6 6 0 006 0zm2.12 4.88L5.5 7.5 3.88 5.88a.5.5 0 01.7-.7l.92.92 2-2a.5.5 0 01.7.7z" />
        </svg>
      );
    case ActivityType.ASSIGNED:
      return (
        <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
          <path d="M6 6a2.5 2.5 0 100-5 2.5 2.5 0 000 5zm0 1c-2.5 0-5 1.25-5 2.5V11h10V9.5c0-1.25-2.5-2.5-5-2.5z" />
        </svg>
      );
    default:
      return (
        <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
          <circle cx="6" cy="6" r="4" />
        </svg>
      );
  }
}

function getActivityMessage(activity: TicketActivity): string {
  if (activity.displayMessage) {
    return activity.displayMessage;
  }

  switch (activity.type) {
    case ActivityType.CREATED:
      return 'Request submitted';
    case ActivityType.STATUS_CHANGE:
      if (activity.newStatus) {
        return getUserFriendlyStatus(activity.newStatus);
      }
      return 'Status updated';
    case ActivityType.ASSIGNED:
      return 'Assigned to IT Support Team';
    case ActivityType.RESOLVED:
      return 'Request resolved';
    case ActivityType.REOPENED:
      return 'Request reopened';
    default:
      return '';
  }
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
